import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ButtonWithLoading } from '@actual-app/components/button';
import { Input } from '@actual-app/components/input';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';

import { Error } from '#components/alerts';
import { Link } from '#components/common/Link';
import {
  Modal,
  ModalButtons,
  ModalCloseButton,
  ModalHeader,
} from '#components/common/Modal';
import { FormField, FormLabel } from '#components/forms';
import type { Modal as ModalType } from '#modals/modalsSlice';
import { getSecretsError } from '#util/error';

type OpenExchangeRatesInitialiseModalProps = Extract<
  ModalType,
  { name: 'openexchangerates-init' }
>['options'];

export const OpenExchangeRatesInitialiseModal = ({
  onSuccess,
}: OpenExchangeRatesInitialiseModalProps) => {
  const { t } = useTranslation();
  const [appId, setAppId] = useState('');
  const [isValid, setIsValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(t('It is required to provide an App ID.'));

  const onSubmit = async (close: () => void) => {
    const value = appId.trim();
    if (!value) {
      setIsValid(false);
      return;
    }

    setIsLoading(true);

    const { error, reason } =
      (await send('secret-set', {
        name: 'openexchangerates_appId',
        value,
      })) || {};

    setIsLoading(false);

    if (error) {
      setIsValid(false);
      setError(getSecretsError(error, reason));
      return;
    }

    onSuccess();
    close();
  };

  return (
    <Modal
      name="openexchangerates-init"
      containerProps={{ style: { width: 320 } }}
    >
      {({ state }) => (
        <>
          <ModalHeader
            title={t('Set-up Open Exchange Rates')}
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />
          <View style={{ display: 'flex', gap: 10 }}>
            <Text>
              <Trans>
                Exchange rates can be fetched automatically from{' '}
                <Link
                  variant="external"
                  to="https://openexchangerates.org/signup/free"
                  linkColor="purple"
                >
                  Open Exchange Rates
                </Link>
                . Sign up for an account and paste your App ID below. The free
                plan (1,000 requests a month, hourly rates) is plenty for daily
                fetching.
              </Trans>
            </Text>

            <FormField>
              <FormLabel title={t('App ID:')} htmlFor="app-id-field" />
              <Input
                id="app-id-field"
                type="password"
                value={appId}
                onChangeValue={value => {
                  setAppId(value);
                  setIsValid(true);
                }}
              />
            </FormField>

            {!isValid && <Error>{error}</Error>}
          </View>

          <ModalButtons>
            <ButtonWithLoading
              variant="primary"
              autoFocus
              isLoading={isLoading}
              onPress={() => {
                void onSubmit(() => state.close());
              }}
            >
              <Trans>Save and continue</Trans>
            </ButtonWithLoading>
          </ModalButtons>
        </>
      )}
    </Modal>
  );
};
